import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface Props {
  open: boolean;
  entityLabel?: string;
  value: string;
  loading?: boolean;
  onChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

const SnackableRegenerateDialog: React.FC<Props> = ({
                                                      open,
                                                      entityLabel,
                                                      value,
                                                      loading = false,
                                                      onChange,
                                                      onClose,
                                                      onConfirm,
                                                    }) => {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{loading ? 'Regenerating Draft…' : 'Regenerate Draft'}</DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {entityLabel ? (
            <Alert severity="info">
              {loading ? (
                <>
                  Generating updated draft for <strong>{entityLabel}</strong>
                </>
              ) : (
                <>
                  You are about to regenerate the draft for <strong>{entityLabel}</strong>
                </>
              )}
            </Alert>
          ) : null}

          {!loading ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Provide additional guidance for what should change in the regenerated draft.
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={16}
                autoFocus
                label="What changes are needed?"
                placeholder="Example: Make the Configure section more technical, reduce marketing language, improve validation steps, add more competitive differentiators, and increase quiz difficulty."
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={loading}
              />

              <Typography variant="caption" color="text.secondary">
                Your guidance will be included in the regeneration request and used to refine the next draft.
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} />
                <Typography variant="body1" fontWeight={500}>
                  Regeneration submitted
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                The updated draft is being generated now. This can take a couple of minutes depending on content size and AI processing time.
              </Typography>

              <Alert severity="warning">
                The current saved draft will remain visible until the new regenerated draft finishes and is written back.
              </Alert>

              {value?.trim() ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Regeneration guidance submitted
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Box>
              ) : null}

              <Typography variant="caption" color="text.secondary">
                You can close this dialog after submission if you want. The course manager will refresh when the job completes.
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        {!loading ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={onConfirm}>
              Regenerate Draft
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>
              Close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SnackableRegenerateDialog;